import React, {useEffect, useRef, useState} from 'react';

import {BaseModal} from 'components/modals';
import Button from '@/components/ui/Button';
import useData from 'utils/useData';
import {AggregationChoice, EventFilter, EventFilterGroup} from '@/views/storyboard/utils/chartConstants';
import {TableChartData} from '@/views/storyboard/cellhandlers/TableCellHandler';
import {BaseChartData, MetricAggregationSingleNumberChartData} from 'entities/chartData';
import {ChartOutput} from 'entities/chart';
import TableComponent from '@/components/table/Table';
import {ColumnDef} from '@tanstack/react-table';
import {useStoreActions} from 'easy-peasy';
import i18n from 'i18n';

interface Props {
    width: number;
    height: number;
    data: TableChartData[];
    chartId: number | null;
    chartTitle: string;
    distinctKeys: string[];
    aggregationField: string | null;
    output?: ChartOutput;
    aggregationFields?: AggregationChoice[];
}

export const TableContent = React.memo<Props>((
    {
        width,
        height,
        data,
        chartId,
        chartTitle,
        distinctKeys,
        aggregationField,
        output,
        aggregationFields = [],
    },
) => {
    const [eventFilterGroupList] = useData<EventFilterGroup>(window.URLS.eventFiltersResource, {
        storeResolver: 'eventFilters',
        actionResolver: 'loadEventFilters',
        useCachedData: true,
    });

    const eventFilters: EventFilter[] = [
        ...(eventFilterGroupList.core || []),
        ...(eventFilterGroupList.prioritised || []),
        ...(eventFilterGroupList.non_prioritised || []),
        ...(eventFilterGroupList.others || []),
    ];

    const innerContentRef = useRef<HTMLDivElement>(null);

    const [showMoreButtonVisible, setShowMoreButtonVisible] = useState(false);
    const [showMoreModalOpen, setShowMoreModalOpen] = useState(false);

    const firstDataPoint = data[0];
    const keys = Object.keys(firstDataPoint);
    let haveKeyColumn = true;

    let dataKeyLabel: string | number = keys.includes('dataLabel') ? 'dataLabel' : 'labelFull';

    const dataKeyLabelName = keys.includes('labelName') ? (firstDataPoint as BaseChartData).labelName : dataKeyLabel;
    if (keys.includes('singleNumber')) {
        dataKeyLabel = (data as MetricAggregationSingleNumberChartData[])?.[0]?.Result || 0;
        haveKeyColumn = false;
    }

    if (
        typeof dataKeyLabel === 'string'
        && !Object.keys(firstDataPoint).includes(dataKeyLabel)
        && Object.keys(firstDataPoint).includes('key')
    ) {
        dataKeyLabel = 'key';
    }

    let aggregationTitle = '';
    if (aggregationField) {
        aggregationTitle = eventFilters.find((x) => x[0] === aggregationField)?.[1] ?? '';
    }

    const keyColumns = [];
    const label = aggregationField ? aggregationTitle : dataKeyLabelName;
    const isMultiAggregationTable = output === ChartOutput.MultiTerms && aggregationFields.length > 0;
    if (isMultiAggregationTable) {
        aggregationFields.forEach((field, index) => {
            keyColumns.push({
                id: `multiAggregation-${index}`,
                header: field.label,
                accessorKey: `multiAggregation_${index}`,
            });
        });
    } else if (haveKeyColumn) {
        keyColumns.push({
            id: 'keyColumn',
            header: label.toString(),
            accessorKey: dataKeyLabel.toString(),
        });
    }

    const offsetHeight = innerContentRef.current?.offsetHeight;

    useEffect(() => {
        if (offsetHeight && offsetHeight > height) {
            setShowMoreButtonVisible(true);
        } else {
            setShowMoreButtonVisible(false);
        }
    }, [
        offsetHeight,
        data,
        width,
        height,
        setShowMoreButtonVisible,
    ]);

    const keyColumnString = (x: string): string => {
        if (x === 'labelFullComparable') {
            return i18n.t('tc_comparable_date.text');
        }

        if (x === 'docCount') {
            return i18n.t('tc_result.text');
        } else {
            return i18n.t('tc_comparable_result.text');
        }
    };

    // Assuming keyColumns and distinctKeys are already defined
    const columns: ColumnDef<any>[] = [
        ...keyColumns,
        ...distinctKeys.map((key) => ({
            id: key,
            accessorKey: key,
            header: ['docCount', 'comparableDocCount', 'labelFullComparable'].includes(key) ? keyColumnString(key) : key,
        })),
    ];

    const tableData = isMultiAggregationTable
        ? data.map((row) => {
            const keyValue = String(
                (row as Record<string, unknown>).labelFull
                ?? (row as Record<string, unknown>).key
                ?? '',
            );
            const keyParts = keyValue.split('|');

            return aggregationFields.reduce<Record<string, unknown>>(
                (nextRow, _field, index) => ({
                    ...nextRow,
                    [`multiAggregation_${index}`]: keyParts[index] ?? '',
                }),
                {...row},
            );
        })
        : data;

    const table = <TableComponent data={tableData} columns={columns} tableClassName="storyboard-chart-table" />;

    // @ts-ignore
    const convertToCSV = (data, columns) => {
        const csvData = [
            // @ts-ignore
            columns.map(col => col.header).join(','),
            // @ts-ignore
            ...tableData.map(row => columns.map(col => row[col.accessorKey]).join(','))
        ].join('\n');
        return csvData;
    };

    // @ts-ignore
    const storeCSVDownloadCallback = useStoreActions((actions) => actions.tableChartDownloadCallbacks.setCallback);
    // @ts-ignore
    const removeCSVDownloadCallback = useStoreActions((actions) => actions.tableChartDownloadCallbacks.removeCallback);

    useEffect(() => {
        if (chartId) {
            const csvData = convertToCSV(tableData, columns);
            storeCSVDownloadCallback({
                [chartId]: () => {
                    // Define your download logic here
                    const blob = new Blob([csvData], {type: 'text/csv'});
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `${chartTitle}.csv`;
                    a.click();
                }
            });

            return () => removeCSVDownloadCallback(chartId);
        }
    }, [chartId, storeCSVDownloadCallback, removeCSVDownloadCallback, tableData, columns, chartTitle]);

    return (
        <div
            className="px-2 comment-wrapper position-relative overflow-hidden"
            style={{
                width,
                height: height - 16,
            }}
        >
            <div className="top-scrollbar-outer">
                <div className="top-scrollbar-inner" />
            </div>
            <div className="chart-table-container table-responsive position-relative overflow-scroll" style={{
                display: 'grid',
                position: 'absolute',
                width: '100%',
            }}>
                <div ref={innerContentRef}>
                    {table}
                </div>
            </div>
            {
                showMoreButtonVisible ? (
                    <div className="show-more-button position-absolute w-100 pl-3">
                        <Button
                            variant="link"
                            size="sm"
                            className="plain-button position-absolute text-info"
                            onClick={() => setShowMoreModalOpen(true)}
                        >
                            {i18n.t('jt_show_all.text')}
                        </Button>
                    </div>
                ) : null
            }

            <BaseModal
                isOpen={showMoreModalOpen}
                onClose={() => setShowMoreModalOpen(false)}
                title={chartTitle}
                size="xl"
                dialogClassName="fixed-height-modal modal-80w"
            >
                <div className="fixed-header-table-wrapper">
                    {table}
                </div>
            </BaseModal>
        </div>
    );
});
